import {env} from '../config/env.js';
import {authedFetch} from '../auth/auth-fetch.js';

export interface BackendResult<T> {
	ok: boolean;
	data?: T;
	error?: string;
	statusCode?: number;
}

export interface EmailTemplateDraft {
	subject?: string;
	customIntro?: string;
	customInstructions?: string;
	includesQr?: boolean;
	includesPrivateData?: boolean;
	includesConfirmButton?: boolean;
	primaryColor?: string;
	accentColor?: string;
}

async function backendFetch<T>(path: string, options: {method?: string; body?: unknown} = {}): Promise<BackendResult<T>> {
	const url = `${env.CLAM_BACKEND_URL}${path}`;
	try {
		const res = await authedFetch(url, {
			method: options.method || 'GET',
			body: options.body ? JSON.stringify(options.body) : undefined,
			headers: {'Content-Type': 'application/json'},
			audience: 'clam',
		});
		const text = await res.text();
		let json: unknown = null;
		try {
			json = text ? JSON.parse(text) : null;
		} catch {
			json = null;
		}
		if (!res.ok) {
			return {ok: false, error: (json as {error?: string})?.error || `HTTP ${res.status}`, statusCode: res.status};
		}
		return {ok: true, data: json as T};
	} catch (e) {
		return {ok: false, error: e instanceof Error ? e.message : String(e)};
	}
}

export interface InvitationRow {
	id: string;
	personaId: string;
	personaName: string;
	personaEmail: string;
	personaEmpresa: string;
	personaRol: string;
	qrToken: string;
	qrDataUrl: string;
	enviado: boolean;
	enviosCount: number;
	ultimoEnvio: string | null;
	confirmado: boolean;
	confirmadoAt: string | null;
}

export interface InvitationListResponse {
	eventoId: string;
	total: number;
	enviados: number;
	confirmados: number;
	data: InvitationRow[];
}

export interface GenerateResult {
	generated: number;
	skipped: number;
}

export interface SendResult {
	sent: number;
	failed: number;
	total: number;
	details: {invitationId: string; email: string; success: boolean; error?: string}[];
}

export interface ConfirmResult {
	personaName: string;
	eventName: string;
	alreadyConfirmed: boolean;
}

export interface SyncResult {
	created?: number;
	updated?: number;
	total?: number;
	message?: string;
	[key: string]: unknown;
}

export interface GafeteImportResult {
	total: number;
	matched: number;
	updated: number;
	skipped: number;
	notFound: string[];
	ambiguous: string[];
	errors: string[];
}

export interface GafeteRow {
	nombre: string;
	email: string;
	gafete: string;
}

export interface EventTemplateListItem {
	id: string;
	name: string;
	subject?: string;
	includesQr?: boolean;
	includesPrivateData?: boolean;
}

export interface InvitationExportRow {
	id: string;
	name: string;
	email: string;
	phone: string;
	empresa: string;
	rol: string;
	puesto: string;
	tipoOrganizacion: string;
	estado: string;
	municipio: string;
	esMiembroClam: boolean;
	alergias: string;
	emailInstitucional: string;
	gafete: string;
	description: string;
	invitationId: string;
	qrToken: string;
	enviado: boolean;
	enviosCount: number;
	ultimoEnvio: string | null;
	confirmado: boolean;
	confirmadoAt: string | null;
}

export const clamBackend = {
	generateInvitations: (eventoId: string) =>
		backendFetch<GenerateResult>(`/api/invitations/generate/${eventoId}`, {method: 'POST', body: {}}),

	listInvitations: (eventoId: string) =>
		backendFetch<InvitationListResponse>(`/api/invitations/${eventoId}`),

	listEventTemplates: (eventoId: string) =>
		backendFetch<{eventoId: string; data: EventTemplateListItem[]}>(`/api/invitations/${eventoId}/templates`),

	sendInvitations: (eventoId: string, body: {onlyUnsent?: boolean; invitationIds?: string[]; templateId?: string} = {}) =>
		backendFetch<SendResult>(`/api/invitations/send/${eventoId}`, {method: 'POST', body}),

	sendOneInvitation: (invitationId: string, templateId?: string) =>
		backendFetch<{sent: boolean; email: string; error?: string}>(`/api/invitations/send-one/${invitationId}`, {method: 'POST', body: {templateId}}),

	sendBatch: (eventoId: string, body: {invitationIds?: string[]; onlyUnsent?: boolean; templateId?: string; concurrency?: number} = {}) =>
		backendFetch<SendResult>(`/api/invitations/send-batch/${eventoId}`, {method: 'POST', body}),

	syncCsv: (eventoId: string) =>
		backendFetch<SyncResult>(`/api/forms/sync/${eventoId}`, {method: 'POST', body: {}}),

	importGafetes: (rows: GafeteRow[]) =>
		backendFetch<GafeteImportResult>(`/api/forms/gafete/import`, {method: 'POST', body: {rows}}),

	confirmPublic: (token: string) =>
		backendFetch<ConfirmResult>(`/api/public/confirmar/${token}`),

	previewEmail: (eventoId: string, templateId?: string) =>
		backendFetch<{html: string; subject: string}>(`/api/email/preview`, {method: 'POST', body: {eventoId, templateId}}),

	previewEmailTemplate: (eventoId: string, template: EmailTemplateDraft) =>
		backendFetch<{html: string; subject: string}>(`/api/email/preview`, {method: 'POST', body: {eventoId, template}}),

	exportInvitations: (eventoId: string) =>
		backendFetch<{eventoId: string; total: number; data: InvitationExportRow[]}>(`/api/invitations/${eventoId}/export`),

	previewInvitationEmail: (invitationId: string, templateId?: string) =>
		backendFetch<{html: string; subject: string}>(`/api/email/preview-invitation`, {method: 'POST', body: {invitationId, templateId}}),

	registerPublic: async (eventoId: string, body: Record<string, string>) => {
		const url = `${env.CLAM_BACKEND_URL}/api/public/register/${eventoId}`;
		const res = await fetch(url, {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			body: JSON.stringify(body),
		});
		return res.json();
	},
};
